import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import axios from 'axios';

/**
 * Reason-code behavior shared by the three AR actions that carry a reason: post adjustment,
 * write off, and void. Each is its own modal, so the configuration read and the three states it
 * resolves to live here rather than in each of them.
 */
@Component
export default class ARReasonCodesMixin extends Vue {
    protected reasonCodes: string[] = [];
    protected requireReasonCode = false;

    /**
     * Reasons come from the configured list whenever the pharmacy has one. A pharmacy that has not
     * configured codes and has not opted into the requirement keeps the original free-text input,
     * so this does not strand it — the API still only checks that a reason is present.
     */
    get useReasonCodeList(): boolean {
        return this.reasonCodes.length > 0 || this.requireReasonCode;
    }

    /** Reason required, but nothing to pick: an admin misconfiguration the user cannot work around. */
    get reasonCodesUnavailable(): boolean {
        return this.requireReasonCode && this.reasonCodes.length === 0;
    }

    get reasonCodeOptions(): { value: string; text: string }[] {
        return [
            { value: "", text: this.reasonCodes.length ? "Select a reason code..." : "No reason codes configured" },
            ...this.reasonCodes.map(code => ({ value: code, text: code })),
        ];
    }

    get reasonCodeLabel(): string {
        return this.requireReasonCode ? "Reason Code *" : "Reason Code";
    }

    protected async loadReasonCodeConfiguration(): Promise<void> {
        // Independent settings, so fetch them together rather than serially. Failing open on either
        // leaves the server-side check as the backstop rather than blocking AR work on a hiccup.
        const [codes, required] = await Promise.all([
            axios.get<unknown>("/configuration/arStatementReasonCodes")
                .then(r => this.parseReasonCodes(r.data)).catch(() => []),
            axios.get<boolean>("/configuration/arRequireReasonCode")
                .then(r => r.data === true).catch(() => false),
        ]);
        this.reasonCodes = codes;
        this.requireReasonCode = required;
    }

    /**
     * The configuration arrives after the reason field renders, so a value typed in that window may
     * not exist in the list the picker is about to switch to: the select cannot display it, but the
     * submit button still reads it as filled in. Callers pass their reason through this after
     * awaiting the load. A field that stays free text keeps whatever was typed.
     */
    knownReasonCode(reasonCode: string): string {
        if (!this.useReasonCodeList) return reasonCode;
        return this.reasonCodes.includes(reasonCode) ? reasonCode : "";
    }

    /**
     * The single-field configuration endpoint reads the column with Dapper's scalar mapper, which
     * bypasses the string[] JSON type handler the full-configuration read uses. So this arrives as
     * the raw JSON text rather than an array. Accept either shape.
     */
    parseReasonCodes(data: unknown): string[] {
        let value = data;
        if (typeof value === "string") {
            try {
                value = JSON.parse(value);
            } catch {
                return [];
            }
        }
        if (!Array.isArray(value)) return [];
        return value.filter(code => typeof code === "string" && code.trim().length > 0);
    }
}
